import {
  DeleteOutlined,
  DownloadOutlined,
  PauseOutlined,
  RightOutlined
} from "@ant-design/icons"
import { Alert, App, Button, Flex, Space, Typography } from "antd"
import dayjs from "dayjs"
import React, { useState } from "react"

import { type RequestAccountDeletionParams } from "../../../api/types.ts"
import { DeleteAccountModal } from "./DeleteAccountModal.tsx"

const { Text } = Typography

interface AccountActionsSectionProps {
  userId: string
  canDeleteAccount: boolean
  accountDeletionRequestedAt?: string | null
  deleteAccountFetching: boolean
  onRequestAccountDeletion: (
    params: RequestAccountDeletionParams
  ) => Promise<unknown>
}

export function AccountActionsSection({
  userId,
  canDeleteAccount,
  accountDeletionRequestedAt,
  deleteAccountFetching,
  onRequestAccountDeletion
}: AccountActionsSectionProps) {
  const { modal } = App.useApp()
  const [showDeleteModal, setShowDeleteModal] = useState(false)

  const deletionPending = Boolean(accountDeletionRequestedAt)

  const onExportClick = () => {
    modal.confirm({
      centered: true,
      title: "Confirm Project Export",
      content: (
        <Space wrap size={"small"}>
          <Text>Are you sure you want to export your account data?</Text>
        </Space>
      ),
      cancelText: "Cancel",
      okText: "Confirm",
      onOk: () => alert("click")
    })
  }

  const onPauseClick = () => {
    modal.confirm({
      centered: true,
      title: "Confirm Pause Account",
      content: (
        <Space wrap size={"small"}>
          <Text>Are you sure you want to pause your account?</Text>
        </Space>
      ),
      cancelText: "Cancel",
      okText: "Confirm",
      onOk: () => alert("click")
    })
  }

  const handleSubmitDeletion = async (params: RequestAccountDeletionParams) => {
    await onRequestAccountDeletion(params)
    setShowDeleteModal(false)
  }

  return (
    <div className="space-y-3">
      <DeleteAccountModal
        open={showDeleteModal}
        loading={deleteAccountFetching}
        onCancel={() => setShowDeleteModal(false)}
        onSubmit={handleSubmitDeletion}
      />
      {!canDeleteAccount && (
        <Alert
          type="info"
          showIcon
          message="Deletion not available"
          description="Administrator accounts cannot be deleted from this screen. Contact platform support if you need to remove an admin user."
        />
      )}
      {deletionPending && (
        <Alert
          type="warning"
          showIcon
          message="Account deletion requested"
          description={
            accountDeletionRequestedAt
              ? `You submitted a deletion request on ${dayjs(accountDeletionRequestedAt).format("MMM D, YYYY [at] h:mm A")}. If this was a mistake, contact support.`
              : "You have a pending account deletion request. If this was a mistake, contact support."
          }
        />
      )}

      {/* Export Account Data */}
      <Button
        icon={<DownloadOutlined />}
        className={"w-full"}
        onClick={onExportClick}
      >
        <Flex justify={"space-between"} align={"center"} className={"w-full"}>
          Export Account Data
          <RightOutlined />
        </Flex>
      </Button>

      {/* Temporarily Deactivate */}
      <Button
        icon={<PauseOutlined />}
        className={"w-full"}
        onClick={onPauseClick}
      >
        <Flex justify={"space-between"} align={"center"} className={"w-full"}>
          Temporarily Deactivate
          <RightOutlined />
        </Flex>
      </Button>

      {/* Delete Account */}
      {canDeleteAccount && (
        <Button
          onClick={() => setShowDeleteModal(true)}
          icon={<DeleteOutlined />}
          danger
          className={"w-full"}
          disabled={!userId || deletionPending}
          loading={deleteAccountFetching}
        >
          <Flex justify={"space-between"} align={"center"} className={"w-full"}>
            {deletionPending ? "Deletion request submitted" : "Delete Account"}
            <RightOutlined />
          </Flex>
        </Button>
      )}
    </div>
  )
}
